<template>
  <AppDialog
    v-model="dialog"
    title="Export Sightings as CSV"
    max-width="1000px"
    scrollable
    @close="close"
  >
    <v-card-text>
      <v-row class="pt-6">
        <v-col>
          <div class="text-black-accent-1">
            Filter the table below by sighting number or whale, then select rows to export. The export includes every sighting row of each selected sighting.
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6">
          <v-autocomplete
            v-model="sightingFilter"
            :items="sightingNumberItems"
            label="Filter by sighting"
            variant="outlined"
            density="compact"
            multiple
            chips
            closable-chips
            clearable
          />
        </v-col>
        <v-col cols="6">
          <v-autocomplete
            v-model="whaleFilter"
            :items="whaleIdItems"
            label="Filter by whale"
            variant="outlined"
            density="compact"
            multiple
            chips
            closable-chips
            clearable
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-data-table
            v-model="selectedRows"
            :headers="tableHeaders"
            :items="filteredSightings"
            item-value="id"
            density="compact"
            show-select
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div class="text-black-accent-1">
            {{ filteredSightingNumbers.length }} sighting(s) shown,
            {{ selectedSightingNumbers.length }} selected.
          </div>
          <v-progress-linear
            indeterminate
            :active="exporting"
            color="primary"
          />
          <v-alert
            v-if="message"
            type="error"
          >
            {{ message }}
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-btn @click="close">
        <v-icon>mdi-close</v-icon>
        Close
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        class="mr-2"
        :disabled="exporting || filteredSightingNumbers.length === 0"
        @click="exportFiltered"
      >
        <v-icon>mdi-download</v-icon>
        Export Filtered
      </v-btn>
      <v-btn
        color="primary"
        :disabled="exporting || selectedSightingNumbers.length === 0"
        @click="exportSelected"
      >
        <v-icon>mdi-check</v-icon>
        Export Selected
      </v-btn>
    </v-card-actions>
  </AppDialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AppDialog from "@/components/AppDialog";

export default {
  components: { AppDialog },
  data() {
    return {
      sightingFilter: [],
      whaleFilter: [],
      selectedRows: [],
      tableHeaders: [
        { title: "Sighting Number", sortable: true, key: "sightingNumber" },
        { title: "Whale ID", sortable: true, key: "whaleId" },
        { title: "Whale Name", sortable: true, key: "whaleName" },
        { title: "Species", sortable: true, key: "species" },
        { title: "Time", sortable: true, key: "sightingTime" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      dialog: "sighting/export/dialog",
      sightings: "sighting/export/sightings",
      exporting: "sightings/exporting",
      message: "sightings/exportMessage",
    }),
    sightingNumberItems() {
      const numbers = new Set();
      this.sightings.forEach((s) => {
        if (s.sightingNumber) numbers.add(s.sightingNumber);
      });
      return [...numbers].sort();
    },
    whaleIdItems() {
      const ids = new Set();
      this.sightings.forEach((s) => {
        if (s.whaleId && s.whaleId !== "na") ids.add(s.whaleId);
      });
      return [...ids].sort();
    },
    filteredSightings() {
      const sightingSet = new Set(this.sightingFilter);
      const whaleSet = new Set(this.whaleFilter);
      const noSightingFilter = sightingSet.size === 0;
      const noWhaleFilter = whaleSet.size === 0;
      if (noSightingFilter && noWhaleFilter) {
        return this.sightings;
      }
      return this.sightings.filter((s) => {
        const sightingMatch = noSightingFilter || sightingSet.has(s.sightingNumber);
        const whaleMatch = noWhaleFilter || whaleSet.has(s.whaleId);
        return sightingMatch && whaleMatch;
      });
    },
    filteredSightingNumbers() {
      const numbers = new Set();
      this.filteredSightings.forEach((s) => {
        if (s.sightingNumber) numbers.add(s.sightingNumber);
      });
      return [...numbers];
    },
    selectedSightingNumbers() {
      const rowSet = new Set(this.selectedRows);
      const numbers = new Set();
      this.filteredSightings.forEach((s) => {
        if (rowSet.has(s.id) && s.sightingNumber) {
          numbers.add(s.sightingNumber);
        }
      });
      return [...numbers];
    },
  },
  watch: {
    dialog(dialog) {
      if (!dialog) {
        this.sightingFilter = [];
        this.whaleFilter = [];
        this.selectedRows = [];
      }
    },
    sightingFilter() {
      this.selectedRows = [];
    },
    whaleFilter() {
      this.selectedRows = [];
    },
  },
  methods: {
    ...mapActions({
      close: "sighting/export/close",
      exportCsv: "sightings/exportCsv",
    }),
    async exportSelected() {
      await this.exportCsv(this.selectedSightingNumbers);
      if (!this.message) this.close();
    },
    async exportFiltered() {
      await this.exportCsv(this.filteredSightingNumbers);
      if (!this.message) this.close();
    },
  },
};
</script>

<style scoped>
</style>
